import * as React from "react";
import { withPrismicPreview } from "gatsby-plugin-prismic-previews";
import { graphql, PageProps } from "gatsby";
import { DEFAULT_LANGUAGE } from "@config/consts";
import { AVAILABLE_COUNTRIES, HERO_TYPES, ORIGIN } from "@typings/globals";
import theme from "@ui/themes/default";
import { langFromTag } from "@services/LangFromTag/LangFromTag";
import { SmartlookClient } from "@containers/SmartlookClient";
import { useBreakpoint } from "@hooks/useBreakpoint";

import { getServiceType } from "../components/StartBookingForm/StartBookingForm.helpers";
import Layout from "../components/Layout";
import SEO, { SeoProps } from "../components/SEO";

import {
  TextSection,
  QuoteSection,
  VideoSection,
  BookCleaningSection,
  BecomeBatmaidSection,
  HeroSection,
  TextSectionProps,
  SliceHeroProps,
  HeroProps,
  QuoteSectionProps,
  VideoProps,
  BecomeBatmaidProps,
  BookCleaningProps,
  Partners,
  PartnersProps,
  BoxesSection,
  BoxesProps,
  DefaultCard,
  BlogCardProps,
  HowItWorks,
} from "./Homepage/";

enum SECTION_TYPES {
  TEXT_SECTION = "text_section",
  BOXES_LIST = "boxes_list",
  CLEANING_TIPS = "cleaning_tips",
  STEPS_LIST = "steps_list",
}

interface CustomServicePageProps {
  section: any; //SectionsSlices; //@TODO
  index: number;
  language: string;
  howItWorksIconsSize: number;
}

// @TODO: adjust typing
const CustomServicePage = ({
  section,
  index,
  language,
  howItWorksIconsSize,
}: CustomServicePageProps): React.ReactElement => {
  switch (section.slice_type) {
    case SECTION_TYPES.TEXT_SECTION: {
      const textSectionData: TextSectionProps = {
        text_section_title: section.primary.text_section_title,
        text_section_text: section.primary.text_section_text,
        isWhite: index % 2 === 0,
      };

      return <TextSection {...textSectionData} />;
    }
    case SECTION_TYPES.BOXES_LIST: {
      const boxesData: BoxesProps<DefaultCard> = {
        section_title: section.primary?.section_title,
        items: section.items?.map((box: any) => ({
          box_image_text: box?.box_image_text,
          box_image: box?.box_image,
          service_link: box?.service_link,
        })),
        language,
        isWhite: index % 2 === 0,
      };

      return <BoxesSection {...boxesData} />;
    }
    case SECTION_TYPES.CLEANING_TIPS: {
      const cleaningTipsData: BoxesProps<BlogCardProps> = {
        section_title: section.primary.section_title,
        items: section.items,
        isBlog: true,
        isWhite: index % 2 === 0,
      };

      return <BoxesSection {...cleaningTipsData} />;
    }

    case SECTION_TYPES.STEPS_LIST: {
      const stepsListData = {
        ...section.primary,
        isWhite: index % 2 === 0,
        iconsColor: theme.palette.grey600,
      };

      return <HowItWorks {...stepsListData} iconsSize={howItWorksIconsSize} />;
    }

    default: {
      return <React.Fragment />;
    }
  }
};

type Primary = TextSectionProps | Omit<BoxesProps<DefaultCard>, "items">;
type Items = DefaultCard[] | BlogCardProps[];

interface SectionsSlices {
  slice_type: SECTION_TYPES;
  primary: Partial<Primary>;
  items: Partial<Items>;
}

type Props = PageProps<
  {
    prismicServicePage: {
      data: PageData;
    };
  },
  {
    lang: string;
    origin: ORIGIN;
    country: AVAILABLE_COUNTRIES;
    type: string;
  }
>;

interface PageData
  extends HeroProps,
    PartnersProps,
    QuoteSectionProps,
    VideoProps,
    BecomeBatmaidProps,
    BookCleaningProps,
    SeoProps {
  title: {
    text: string;
  };
  slug: {
    text: string;
  };
  service_type: HERO_TYPES;
  body: SliceHeroProps[];
  body1: SectionsSlices[];
}

const ServicePage = (props: Props): React.ReactElement => {
  const { pageContext, data } = props;
  const { prismicServicePage } = data;

  const { desktop, desktopLarge } = useBreakpoint("v1");

  const language = langFromTag(pageContext.lang) || DEFAULT_LANGUAGE;

  const urlLang = pageContext.lang.split("-")[0];

  const seoData: SeoProps = {
    seo_title: prismicServicePage.data.seo_title,
    seo_description: prismicServicePage.data.seo_description,
    seo_image: prismicServicePage.data.seo_image,
  };

  const heroData: HeroProps = {
    language,
    hero_image_mobile: prismicServicePage.data.hero_image_mobile,
    booking_form_submit: prismicServicePage.data.booking_form_submit,
    booking_form_placeholder: prismicServicePage.data.booking_form_placeholder,
    email_submit: prismicServicePage.data.email_submit,
    email_placeholder: prismicServicePage.data.email_placeholder,
    heroesList: prismicServicePage.data.body,
    landingUrl: `/${urlLang}`,
    hero_breadcrumb_text: prismicServicePage.data.hero_breadcrumb_text,
  };

  const partnersData = {
    partners_logos: prismicServicePage.data.partners_logos,
    partners_title: prismicServicePage.data.partners_title,
  };

  const whyQuoteData = {
    quote_title: prismicServicePage.data.quote_title,
    quote_quote: prismicServicePage.data.quote_quote,
    quote_author: prismicServicePage.data.quote_author,
  };

  const videoData = {
    video_title: prismicServicePage.data.video_title,
    video_title_bottom: prismicServicePage.data.video_title_bottom,
    video_url: prismicServicePage.data.video_url,
  };

  const readyToBookData: BookCleaningProps = {
    ready_to_book_title: prismicServicePage.data.ready_to_book_title,
    ready_to_book_list: prismicServicePage.data.ready_to_book_list,
    ready_to_book_list_mobile:
      prismicServicePage.data.ready_to_book_list_mobile,
    ready_to_book_image: prismicServicePage.data.ready_to_book_image,
    booking_form_submit: prismicServicePage.data.booking_form_submit,
    booking_form_placeholder: prismicServicePage.data.booking_form_placeholder,
    email_submit: prismicServicePage.data.email_submit,
    email_placeholder: prismicServicePage.data.email_placeholder,
    serviceType: getServiceType(prismicServicePage.data.service_type),
    language,
  };

  const becomeBatmaidData = {
    become_cleaner_title: prismicServicePage.data.become_cleaner_title,
    become_cleaner_quote: prismicServicePage.data.become_cleaner_quote,
    become_cleaner_quote_author:
      prismicServicePage.data.become_cleaner_quote_author,
    become_cleaner_button_text:
      prismicServicePage.data.become_cleaner_button_text,
    become_cleaner_button_link:
      prismicServicePage.data.become_cleaner_button_link,
    lang: pageContext.lang,
    become_cleaner_image: prismicServicePage.data.become_cleaner_image,
  };

  return (
    <Layout
      language={language}
      country={props.pageContext.country}
      origin={ORIGIN.BATMAID}
      isHomeCleaningCTAEnabled={
        prismicServicePage.data.service_type === HERO_TYPES.HOME_CLEANING
      }
      isFullWidth
    >
      <SmartlookClient />
      <SEO
        {...seoData}
        title={prismicServicePage.data.title.text}
        breadcrumbs={[
          {
            title:
              seoData.seo_title?.text || prismicServicePage.data.title.text,
            href: `/${language}/${prismicServicePage.data.slug.text.trim()}`,
          },
        ]}
        path={props.location.pathname}
      />
      <HeroSection {...heroData} isCityPage />
      <Partners
        partners_title={partnersData.partners_title}
        hasPartners={!!partnersData.partners_logos.length}
      />
      {prismicServicePage.data.body1?.map((section, index) => (
        <CustomServicePage
          key={index}
          section={section}
          language={language}
          index={index}
          howItWorksIconsSize={desktop || desktopLarge ? 96 : 64}
        />
      ))}
      <QuoteSection {...whyQuoteData} />
      <VideoSection {...videoData} />
      <BecomeBatmaidSection {...becomeBatmaidData} />
      <BookCleaningSection {...readyToBookData} />
    </Layout>
  );
};

export default withPrismicPreview(ServicePage);

export const query = graphql`
  query ServicePageQuery($id: String) {
    prismicServicePage(id: { eq: $id }) {
      _previewable
      lang
      data {
        slug {
          text
        }
        title {
          text
        }
        # ==== SEO ====
        seo_title {
          text
        }
        seo_description {
          text
        }
        seo_image {
          gatsbyImageData(width: 1200)
        }

        # ==== BOOKING FORM TEXTS ====
        booking_form_submit {
          text
        }
        booking_form_placeholder {
          text
        }

        # ==== EMAIL TEXTS (DEPRECATED) ====
        email_submit {
          text
        }
        email_placeholder {
          text
        }

        # hero
        hero_image_mobile {
          gatsbyImageData
        }
        hero_breadcrumb_text {
          text
        }
        body {
          ... on PrismicServicePageDataBodyLandingHero {
            primary {
              hero_type
              hero_section_title {
                text
              }
              hero_navigation_text {
                text
              }
              hero_section_description {
                text
              }
              hero_background_img {
                gatsbyImageData(width: 1920, imgixParams: { q: 90 })
              }
              button_text {
                text
              }
              button_link {
                text
              }
            }

            items {
              hero_description_image {
                gatsbyImageData
              }
              list_item {
                text
              }
            }
          }
        }

        # ==== PARTNERS SECTION ====
        partners_title {
          text
        }
        partners_logos {
          partner_logo {
            alt
            gatsbyImageData(
              layout: CONSTRAINED
              imgixParams: { q: 100 }
              sizes: "(max-width: 360px) 85px, 144px"
              placeholder: BLURRED
            )
          }
          partner_link {
            url
            target
          }
        }

        # custom section

        body1 {
          ... on PrismicServicePageDataBody1TextSection {
            slice_type
            primary {
              text_section_title {
                text
              }
              text_section_text {
                text
              }
            }
          }
          ... on PrismicServicePageDataBody1CleaningTips {
            slice_type
            primary {
              section_title {
                text
              }
            }
            items {
              blog_card_background_photo {
                gatsbyImageData
              }
              blog_card_title {
                text
              }
              blog_card_hashtags {
                text
              }
              blog_card_link {
                text
              }
            }
          }
          ... on PrismicServicePageDataBody1BoxesList {
            slice_type
            primary {
              section_title {
                text
              }
            }
            items {
              box_image {
                gatsbyImageData(
                  layout: CONSTRAINED
                  imgixParams: { q: 95, minH: 191, fit: "crop", crop: "faces" }
                  sizes: "(max-width: 360px) 280px, (max-width: 767px) 687px, (max-width: 991px) 312px, 232px"
                  placeholder: BLURRED
                )
              }
              box_image_text {
                text
              }
              service_link {
                slug
              }
            }
          }
          ... on PrismicServicePageDataBody1WhatDoWeOffer {
            slice_type
            primary {
              section_title {
                text
              }
            }
            items {
              what_do_we_offer_card_image {
                gatsbyImageData(
                  layout: CONSTRAINED
                  imgixParams: { q: 95, minH: 191, fit: "crop", crop: "faces" }
                  sizes: "(max-width: 360px) 280px, (max-width: 767px) 687px, (max-width: 991px) 312px, 232px"
                  placeholder: BLURRED
                )
              }
              what_do_we_offer_card_image_mobile {
                gatsbyImageData(
                  layout: CONSTRAINED
                  imgixParams: { q: 95, minH: 191, fit: "crop", crop: "faces" }
                  sizes: "(max-width: 360px) 280px, (max-width: 767px) 687px, (max-width: 991px) 312px, 232px"
                  placeholder: BLURRED
                )
              }
              what_do_we_offer_card_title {
                text
              }
              what_do_we_offer_card_title {
                text
              }
              what_do_we_offer_card_list_element__1 {
                text
              }
              what_do_we_offer_card_list_element__2 {
                text
              }
              what_do_we_offer_card_list_element__3 {
                text
              }
              what_do_we_offer_card_list_element__4 {
                text
              }
              what_do_we_offer_card_list_element__5 {
                text
              }
            }
          }
          ... on PrismicServicePageDataBody1StepsList {
            slice_type
            primary {
              how_it_works_title {
                text
              }
              how_it_works_title_mobile {
                text
              }
              how_it_works_step_one_title {
                text
              }
              how_it_works_step_one {
                text
              }
              how_it_works_step_one_icon
              how_it_works_step_two_title {
                text
              }
              how_it_works_step_two {
                text
              }
              how_it_works_step_two_icon
              how_it_works_step_three_title {
                text
              }
              how_it_works_step_three {
                text
              }
              how_it_works_step_three_icon
            }
          }
        }
        # ==== QUOTE SECTION ====
        quote_title {
          text
        }
        quote_quote {
          text
        }
        quote_author {
          text
        }
        # ==== VIDEO SECTION ====
        video_title {
          text
        }
        video_url {
          url
        }
        video_title_bottom {
          text
        }
        # ==== BECOME BATMAID SECTION ====
        become_cleaner_title {
          text
        }
        become_cleaner_quote {
          text
        }
        become_cleaner_quote_author {
          text
        }
        become_cleaner_button_text {
          text
        }
        become_cleaner_button_link {
          text
        }
        become_cleaner_image {
          gatsbyImageData(
            layout: CONSTRAINED
            imgixParams: { q: 95 }
            placeholder: BLURRED
          )
        }
        # ==== BOOK CLEANING SECTION ====
        service_type
        ready_to_book_title {
          text
        }
        ready_to_book_list {
          list_item {
            text
          }
        }
        ready_to_book_list_mobile {
          list_item {
            text
          }
        }
        ready_to_book_image {
          gatsbyImageData(
            layout: CONSTRAINED
            imgixParams: { q: 95 }
            width: 1980
          )
        }
      }
    }
  }
`;
